import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    InputAdornment,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Skeleton,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShareIcon from "@mui/icons-material/Share";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { formatDistanceToNow } from "date-fns";
import FeedbackButtons from "./FeedbackButtons";
import ReportOutput from "./ReportOutput";
import {
    deleteReport,
    shareReport,
    refreshReport,
    getTemplateDashboards,
    sendReportToDashboard,
    parametrizeReport,
    saveReportSqlTemplate,
} from "../ai-reports-service";

const scopeLabels = { private: "Private", org: "Team", public: "Public" };
const scopeColors = { private: "default", org: "primary", public: "success" };

const SavedReports = ({ reports, onReportsChange }) => {
    const [sharingId, setSharingId] = useState(null);
    const [shareScope, setShareScope] = useState("org");
    const [refreshingId, setRefreshingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [snackbar, setSnackbar] = useState(null);
    const [sharedLink, setSharedLink] = useState(null);
    // send-to-dashboard multi-step state
    const [sendingReport, setSendingReport] = useState(null);
    const [sendStep, setSendStep] = useState(1); // 1 = pick scope, 2 = pick dashboard
    const [sendData, setSendData] = useState(null); // { personal, teams }
    const [sendLoading, setSendLoading] = useState(false);
    const [selectedScope, setSelectedScope] = useState(null); // "personal" | { teamId, teamName }
    const [selectedDashboard, setSelectedDashboard] = useState("");
    const [parametrizeResult, setParametrizeResult] = useState(null); // { sqlTemplate, detectedParams }
    const [parametrizeLoading, setParametrizeLoading] = useState(false);
    const [makeDynamic, setMakeDynamic] = useState(null); // null | true | false

    const buildLink = (token) => `${window.location.origin}/shared/${token}`;

    const handleOpenSend = async (report) => {
        setSendingReport({ id: report.id, title: report.title, shareScope: report.shareScope });
        setSendStep(1);
        setSendData(null);
        setSelectedScope(null);
        setSelectedDashboard("");
        setParametrizeResult(null);
        setMakeDynamic(null);
        setSendLoading(true);
        const result = await getTemplateDashboards().catch(() => ({ personal: [], teams: [] }));
        setSendData(result || { personal: [], teams: [] });
        setSendLoading(false);
        // Fire parametrize in background so result is ready by the time user reaches step 3
        if (report.sqlQuery) {
            setParametrizeLoading(true);
            parametrizeReport(report.id)
                .then(setParametrizeResult)
                .catch(() => setParametrizeResult({ sqlTemplate: null, detectedParams: [] }))
                .finally(() => setParametrizeLoading(false));
        }
    };

    const handleSelectScope = (scope) => {
        setSelectedScope(scope);
        const scopeDashboards =
            scope === "personal"
                ? sendData?.personal || []
                : sendData?.teams?.find((t) => t.teamId === scope.teamId)?.dashboards || [];
        setSelectedDashboard(scopeDashboards[0]?.id || "");
        setSendStep(2);
    };

    const currentScopeDashboards = () => {
        if (!selectedScope || !sendData) return [];
        if (selectedScope === "personal") return sendData.personal || [];
        return sendData.teams?.find((t) => t.teamId === selectedScope.teamId)?.dashboards || [];
    };

    const handleSend = async () => {
        setSendLoading(true);
        try {
            if (makeDynamic === true && parametrizeResult?.sqlTemplate) {
                await saveReportSqlTemplate(
                    sendingReport.id,
                    parametrizeResult.sqlTemplate,
                    parametrizeResult.detectedParams
                );
            }
            const teamId = selectedScope !== "personal" ? selectedScope?.teamId : undefined;
            await sendReportToDashboard(
                sendingReport.id,
                sendingReport.title,
                selectedDashboard,
                teamId,
                makeDynamic === true
            );
            const dash = currentScopeDashboards().find((d) => d.id === selectedDashboard);
            setSendingReport(null);
            setSnackbar(`Added to ${dash?.title || "dashboard"}!`);
        } catch {
            setSnackbar("Failed to add to dashboard");
        } finally {
            setSendLoading(false);
        }
    };

    const handleDashboardConfirm = () => {
        const hasParams =
            parametrizeResult?.detectedParams?.length > 0 && parametrizeResult?.sqlTemplate;
        if (hasParams && makeDynamic === null) {
            setSendStep(3);
        } else {
            handleSend();
        }
    };

    // Auto-proceed from step 3 once the user has made their dynamic/fixed choice
    useEffect(() => {
        if (makeDynamic !== null && sendStep === 3) {
            handleSend();
        }
    }, [makeDynamic]); // eslint-disable-line

    const handleShare = async () => {
        const updated = await shareReport(sharingId, shareScope);
        onReportsChange((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        if (shareScope === "public" && updated.shareToken) {
            const link = buildLink(updated.shareToken);
            setSharedLink(link);
            navigator.clipboard
                .writeText(link)
                .then(() => setSnackbar("Link copied to clipboard!"));
        } else if (shareScope === "org") {
            const link = `${window.location.origin}/ai-reports?report=${updated.id}`;
            setSharedLink(link);
            navigator.clipboard
                .writeText(link)
                .then(() => setSnackbar("Link copied to clipboard!"));
        } else {
            setSharingId(null);
        }
    };

    const handleRefresh = async (reportId) => {
        setRefreshingId(reportId);
        try {
            await refreshReport(reportId, (event) => {
                if (event.type === "complete" && event.report) {
                    onReportsChange((prev) =>
                        prev.map((r) => (r.id === event.report.id ? event.report : r))
                    );
                }
            });
        } finally {
            setRefreshingId(null);
        }
    };

    const handleDelete = async () => {
        const id = deletingId;
        setDeletingId(null);
        await deleteReport(id);
        onReportsChange((prev) => prev.filter((r) => r.id !== id));
        setSnackbar("Report deleted");
    };

    const handleCopyLink = (token) => {
        const link = buildLink(token);
        navigator.clipboard.writeText(link).then(() => setSnackbar("Link copied to clipboard!"));
    };

    if (reports.length === 0) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height={300}
                color="text.secondary"
            >
                <Typography variant="body1" gutterBottom>
                    No saved reports yet
                </Typography>
                <Typography variant="body2">
                    Ask a question and save the result as a report
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
                {reports.map((report) => (
                    <Box
                        key={report.id}
                        sx={{
                            flex: "1 1 500px",
                            minWidth: 0,
                            maxWidth: "100%",
                            overflow: "hidden",
                        }}
                    >
                        <Card variant="outlined" sx={{ overflow: "hidden" }}>
                            <CardContent>
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    mb={1}
                                >
                                    <Typography variant="subtitle2" fontWeight={600}>
                                        {report.title}
                                    </Typography>
                                    <Chip
                                        label={scopeLabels[report.shareScope]}
                                        size="small"
                                        color={scopeColors[report.shareScope]}
                                        variant="outlined"
                                    />
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.5}
                                >
                                    Created by {report.createdByEmail || report.userId}{" "}
                                    {formatDistanceToNow(new Date(report.createdAt), {
                                        addSuffix: true,
                                    })}
                                    {report.shareScope === "org" && (
                                        <Chip
                                            label="Team"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            sx={{ ml: 1, height: 16, fontSize: 10 }}
                                        />
                                    )}
                                </Typography>
                                {report.lastRefreshedAt && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        mb={1}
                                    >
                                        Refreshed by {report.lastRefreshedByEmail || "unknown"}{" "}
                                        {formatDistanceToNow(new Date(report.lastRefreshedAt), {
                                            addSuffix: true,
                                        })}
                                    </Typography>
                                )}
                                {report.shareScope === "org" &&
                                    (report.feedbackSummary?.upCount ?? 0) >= 2 && (
                                        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                                            <ThumbUpIcon
                                                sx={{ fontSize: 13, color: "success.main" }}
                                            />
                                            <Typography variant="caption" color="success.main">
                                                {report.feedbackSummary.upCount} people found this
                                                useful
                                            </Typography>
                                        </Box>
                                    )}
                                <ReportOutput output={report.output} />
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 1.5 }}>
                                <Tooltip title="Re-run query and refresh data">
                                    <span>
                                        <Button
                                            size="small"
                                            startIcon={
                                                refreshingId === report.id ? (
                                                    <CircularProgress size={12} />
                                                ) : (
                                                    <RefreshIcon />
                                                )
                                            }
                                            disabled={
                                                refreshingId === report.id || !report.sqlQuery
                                            }
                                            onClick={() => handleRefresh(report.id)}
                                        >
                                            Refresh
                                        </Button>
                                    </span>
                                </Tooltip>
                                {report.isOwner && (
                                    <Button
                                        size="small"
                                        startIcon={<ShareIcon />}
                                        onClick={() => {
                                            setShareScope(
                                                report.shareScope === "private"
                                                    ? "org"
                                                    : report.shareScope
                                            );
                                            setSharingId(report.id);
                                        }}
                                    >
                                        Share
                                    </Button>
                                )}
                                {false && (
                                    <Button
                                        size="small"
                                        startIcon={<DashboardIcon />}
                                        onClick={() => handleOpenSend(report)}
                                    >
                                        Send to Dashboard
                                    </Button>
                                )}
                                {report.shareScope === "public" && report.shareToken && (
                                    <Button
                                        size="small"
                                        onClick={() => handleCopyLink(report.shareToken)}
                                    >
                                        Copy Link
                                    </Button>
                                )}
                                {report.shareScope === "org" && (
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            navigator.clipboard
                                                .writeText(
                                                    `${window.location.origin}/ai-reports?report=${report.id}`
                                                )
                                                .then(() =>
                                                    setSnackbar("Link copied to clipboard!")
                                                )
                                        }
                                    >
                                        Copy Link
                                    </Button>
                                )}
                                <Box
                                    sx={{
                                        ml: "auto",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <FeedbackButtons
                                        targetType="report"
                                        targetId={report.id}
                                        initialVote={report.feedbackSummary?.myVote ?? null}
                                        onVoteChange={(newVote) => {
                                            onReportsChange((prev) =>
                                                prev.map((r) => {
                                                    if (r.id !== report.id) return r;
                                                    const prevUp = r.feedbackSummary?.upCount ?? 0;
                                                    const prevMy =
                                                        r.feedbackSummary?.myVote ?? null;
                                                    const delta =
                                                        (newVote === 1 ? 1 : 0) -
                                                        (prevMy === 1 ? 1 : 0);
                                                    return {
                                                        ...r,
                                                        feedbackSummary: {
                                                            upCount: Math.max(0, prevUp + delta),
                                                            myVote: newVote,
                                                        },
                                                    };
                                                })
                                            );
                                        }}
                                    />
                                    {report.isOwner && (
                                        <Tooltip title="Delete report">
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeleteOutlineIcon />}
                                                onClick={() => setDeletingId(report.id)}
                                            >
                                                Delete
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Box>
                            </CardActions>
                        </Card>
                    </Box>
                ))}
            </Box>

            {false && (
                <Box sx={{ px: 3, pb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        💡 Saved reports can be added to any dashboard — use{" "}
                        <strong>Send to Dashboard</strong> above, or go to a dashboard in Edit Mode
                        and use <strong>Add Item → AI Reports</strong>.
                    </Typography>
                </Box>
            )}

            <Dialog open={!!deletingId} onClose={() => setDeletingId(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete report?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will permanently delete the report. Teammates who have the link will
                        lose access.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeletingId(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!sharingId}
                onClose={() => {
                    setSharingId(null);
                    setSharedLink(null);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Share Report</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel>Visibility</InputLabel>
                        <Select
                            value={shareScope}
                            label="Visibility"
                            onChange={(e) => {
                                setShareScope(e.target.value);
                                setSharedLink(null);
                            }}
                        >
                            <MenuItem value="private">Private – only you</MenuItem>
                            <MenuItem value="org">Team – everyone in your org</MenuItem>
                            <MenuItem value="public">Public – anyone with the link</MenuItem>
                        </Select>
                    </FormControl>
                    {sharedLink && (
                        <TextField
                            fullWidth
                            size="small"
                            value={sharedLink}
                            sx={{ mt: 2 }}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy link">
                                            <ContentCopyIcon
                                                fontSize="small"
                                                sx={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    navigator.clipboard
                                                        .writeText(sharedLink)
                                                        .then(() =>
                                                            setSnackbar("Link copied to clipboard!")
                                                        )
                                                }
                                            />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setSharingId(null);
                            setSharedLink(null);
                        }}
                    >
                        Close
                    </Button>
                    {!sharedLink && (
                        <Button variant="contained" onClick={handleShare}>
                            Save
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!sendingReport}
                onClose={() => setSendingReport(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {sendStep === 1 ? (
                        "Send to Dashboard"
                    ) : (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Button
                                size="small"
                                onClick={() => setSendStep(1)}
                                sx={{ minWidth: 0, p: 0.5 }}
                            >
                                ←
                            </Button>
                            {selectedScope === "personal"
                                ? "Personal Dashboards"
                                : `${selectedScope?.teamName} Dashboards`}
                        </Box>
                    )}
                </DialogTitle>
                <DialogContent>
                    {sendingReport?.shareScope === "private" && (
                        <Box
                            sx={{
                                mb: 2,
                                p: 1.5,
                                bgcolor: "warning.50",
                                border: "1px solid",
                                borderColor: "warning.200",
                                borderRadius: 1,
                            }}
                        >
                            <Typography variant="caption" color="warning.dark">
                                This is a private report. Only you will see this tile, even on
                                shared dashboards.
                            </Typography>
                        </Box>
                    )}

                    {/* Step 1 — pick scope */}
                    {sendStep === 1 &&
                        (sendLoading ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                                <Skeleton variant="rounded" height={56} />
                                <Skeleton variant="rounded" height={56} />
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                                <Card
                                    variant="outlined"
                                    onClick={() => handleSelectScope("personal")}
                                    sx={{
                                        cursor: "pointer",
                                        "&:hover": { borderColor: "primary.main" },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            p: 1.5,
                                        }}
                                    >
                                        <PersonIcon color="action" />
                                        <Box>
                                            <Typography variant="body2" fontWeight={600}>
                                                Personal
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {sendData?.personal?.length ?? 0} dashboard
                                                {sendData?.personal?.length !== 1 ? "s" : ""}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Card>
                                {(sendData?.teams || []).map((team) => (
                                    <Card
                                        key={team.teamId}
                                        variant="outlined"
                                        onClick={() => handleSelectScope(team)}
                                        sx={{
                                            cursor: "pointer",
                                            "&:hover": { borderColor: "primary.main" },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                p: 1.5,
                                            }}
                                        >
                                            <GroupsIcon color="action" />
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {team.teamName}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {team.dashboards?.length ?? 0} dashboard
                                                    {team.dashboards?.length !== 1 ? "s" : ""}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                ))}
                            </Box>
                        ))}

                    {/* Step 2 — pick dashboard */}
                    {sendStep === 2 && (
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                            <InputLabel>Dashboard</InputLabel>
                            <Select
                                value={selectedDashboard}
                                label="Dashboard"
                                onChange={(e) => setSelectedDashboard(e.target.value)}
                            >
                                {currentScopeDashboards().map((d) => (
                                    <MenuItem key={d.id} value={d.id}>
                                        {d.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {sendStep === 3 && parametrizeResult && (
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <AutorenewIcon fontSize="small" color="primary" />
                                <Typography variant="body2" fontWeight={600}>
                                    Make this report dynamic?
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                We detected{" "}
                                {[
                                    parametrizeResult.detectedParams.some((p) =>
                                        [
                                            "githubUserIds",
                                            "gitlabUserIds",
                                            "jiraUserIds",
                                            "slackUserIds",
                                            "googleEmails",
                                            "msEmails",
                                        ].includes(p)
                                    ) && "team member filter",
                                    parametrizeResult.detectedParams.some((p) =>
                                        ["dateFrom", "dateTo"].includes(p)
                                    ) && "date range",
                                ]
                                    .filter(Boolean)
                                    .join(" and ")}{" "}
                                in this report. When dynamic, the dashboard will re-run it with its
                                own team and date context.
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={sendLoading}
                                    onClick={() => setMakeDynamic(true)}
                                >
                                    Make dynamic
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={sendLoading}
                                    onClick={() => setMakeDynamic(false)}
                                >
                                    Keep fixed
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {sendStep !== 3 && (
                        <Button onClick={() => setSendingReport(null)}>Cancel</Button>
                    )}
                    {sendStep === 2 && (
                        <Button
                            variant="contained"
                            onClick={handleDashboardConfirm}
                            disabled={!selectedDashboard || sendLoading}
                        >
                            {parametrizeLoading ? "Analysing…" : "Add to Dashboard"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
                message={snackbar}
            />
        </>
    );
};

export default SavedReports;
